import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import Icon from '../Icon'
import { renderPdfPage } from '../../lib/pdfTools'

const clamp = (v, a, b) => Math.max(a, Math.min(b, v))
let uid = 0
const nextId = () => ++uid

const SIZES = { S: 0.018, M: 0.028, L: 0.045 }
const COLORS = ['#14181F', '#E5484D', '#2563EB', '#16A34A', '#F59E0B', '#ffffff']

// Interactive single-page PDF editor. Text / images / freehand drawing are kept
// as page-relative ratios and rasterised to a transparent overlay on export so
// the result matches the on-screen editor exactly.
const PdfEditor = forwardRef(function PdfEditor({ file }, ref) {
  const [bg, setBg] = useState(null) // { dataUrl, width, height }
  const [mode, setMode] = useState('select') // select | draw
  const [texts, setTexts] = useState([])
  const [images, setImages] = useState([])
  const [strokes, setStrokes] = useState([])
  const [selected, setSelected] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [color, setColor] = useState('#14181F')
  const [penWidth, setPenWidth] = useState(3)

  const wrapRef = useRef(null)
  const canvasRef = useRef(null)
  const imgInputRef = useRef(null)
  const drag = useRef(null)
  const drawing = useRef(null)

  // Load the page image.
  useEffect(() => {
    let alive = true
    renderPdfPage(file, 2)
      .then((res) => alive && setBg(res))
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [file])

  // (Re)draw committed strokes onto the display canvas.
  const redraw = () => {
    const canvas = canvasRef.current
    const wrap = wrapRef.current
    if (!canvas || !wrap) return
    const rect = wrap.getBoundingClientRect()
    canvas.width = rect.width
    canvas.height = rect.height
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    strokes.forEach((s) => {
      ctx.strokeStyle = s.color
      ctx.lineWidth = s.w * rect.width
      ctx.beginPath()
      s.pts.forEach((p, i) => {
        const x = p.x * rect.width
        const y = p.y * rect.height
        if (i === 0) ctx.moveTo(x, y)
        else ctx.lineTo(x, y)
      })
      ctx.stroke()
    })
  }
  useEffect(redraw, [strokes, bg])
  useEffect(() => {
    window.addEventListener('resize', redraw)
    return () => window.removeEventListener('resize', redraw)
  })

  // ---- drawing (handlers live on the canvas itself) ----
  const relCanvas = (e) => {
    const c = canvasRef.current
    const r = c ? c.getBoundingClientRect() : { left: 0, top: 0, width: 1, height: 1 }
    return { x: clamp((e.clientX - r.left) / r.width, 0, 1), y: clamp((e.clientY - r.top) / r.height, 0, 1), r }
  }
  const drawStart = (e) => {
    e.preventDefault()
    const { x, y, r } = relCanvas(e)
    drawing.current = { color: color || '#14181F', w: Math.max(1, penWidth) / r.width, pts: [{ x, y }] }
    canvasRef.current?.setPointerCapture?.(e.pointerId)
  }
  const drawMove = (e) => {
    const d = drawing.current
    const canvas = canvasRef.current
    if (!d || !canvas) return
    const { x, y } = relCanvas(e)
    const prev = d.pts[d.pts.length - 1]
    d.pts.push({ x, y })
    const ctx = canvas.getContext('2d')
    ctx.strokeStyle = d.color
    ctx.lineWidth = d.w * canvas.width
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.beginPath()
    ctx.moveTo(prev.x * canvas.width, prev.y * canvas.height)
    ctx.lineTo(x * canvas.width, y * canvas.height)
    ctx.stroke()
  }
  const drawEnd = () => {
    const d = drawing.current
    drawing.current = null
    if (d && d.pts.length > 1) setStrokes((s) => [...s, d])
  }

  // ---- element drag / resize (select mode) ----
  const startDrag = (item, kind, mode2) => (e) => {
    if (mode !== 'select') return
    e.stopPropagation()
    e.preventDefault()
    setSelected({ kind, id: item.id })
    drag.current = {
      kind,
      id: item.id,
      mode: mode2,
      rect: wrapRef.current.getBoundingClientRect(),
      startX: e.clientX,
      startY: e.clientY,
      orig: { ...item },
    }
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }
  const onElementMove = (e) => {
    const d = drag.current
    if (!d) return
    const dx = (e.clientX - d.startX) / d.rect.width
    const dy = (e.clientY - d.startY) / d.rect.height
    const upd = (list, set) =>
      set(list.map((it) => {
        if (it.id !== d.id) return it
        if (d.mode === 'move') return { ...it, xRatio: clamp(d.orig.xRatio + dx, 0, 1), yRatio: clamp(d.orig.yRatio + dy, 0, 1) }
        if (d.kind === 'text') return { ...it, sizeRatio: clamp(d.orig.sizeRatio + (dx + dy), 0.008, 0.3) } // drag to scale text
        return { ...it, wRatio: clamp(d.orig.wRatio + dx, 0.04, 1) } // resize images
      }))
    if (d.kind === 'text') upd(texts, setTexts)
    else upd(images, setImages)
  }
  const onElementUp = () => {
    drag.current = null
  }

  // ---- actions ----
  const addText = () => {
    const t = { id: nextId(), text: 'Double-click to edit', xRatio: 0.3, yRatio: 0.4, sizeRatio: SIZES.M, color }
    setTexts((s) => [...s, t])
    setSelected({ kind: 'text', id: t.id })
    setMode('select')
  }
  const onImageFile = (e) => {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f) return
    const reader = new FileReader()
    reader.onload = () => {
      const im = { id: nextId(), src: reader.result, xRatio: 0.32, yRatio: 0.32, wRatio: 0.35 }
      setImages((s) => [...s, im])
      setSelected({ kind: 'image', id: im.id })
      setMode('select')
    }
    reader.readAsDataURL(f)
  }
  const deleteSelected = () => {
    if (!selected) return
    if (selected.kind === 'text') setTexts((s) => s.filter((t) => t.id !== selected.id))
    else setImages((s) => s.filter((i) => i.id !== selected.id))
    setSelected(null)
  }
  const undoStroke = () => setStrokes((s) => s.slice(0, -1))
  const applyColor = (c) => {
    setColor(c)
    if (selected?.kind === 'text') setTexts((s) => s.map((t) => (t.id === selected.id ? { ...t, color: c } : t)))
  }

  // ---- export overlay PNG (transparent), sized to the page ----
  useImperativeHandle(ref, () => ({
    hasContent: () => texts.length || images.length || strokes.length,
    async exportOverlay() {
      if (!bg) return null
      const W = bg.width
      const H = bg.height
      const canvas = document.createElement('canvas')
      canvas.width = W
      canvas.height = H
      const ctx = canvas.getContext('2d')

      // images
      for (const im of images) {
        const el = await loadImg(im.src)
        const w = im.wRatio * W
        const h = (el.naturalHeight / el.naturalWidth) * w
        ctx.drawImage(el, im.xRatio * W, im.yRatio * H, w, h)
      }
      // text
      texts.forEach((t) => {
        const fs = t.sizeRatio * H
        ctx.font = `600 ${fs}px Helvetica, Arial, sans-serif`
        ctx.fillStyle = t.color
        ctx.textBaseline = 'top'
        t.text.split('\n').forEach((line, i) => {
          ctx.fillText(line, t.xRatio * W, t.yRatio * H + i * fs * 1.15)
        })
      })
      // strokes
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      strokes.forEach((s) => {
        ctx.strokeStyle = s.color
        ctx.lineWidth = s.w * W
        ctx.beginPath()
        s.pts.forEach((p, i) => (i ? ctx.lineTo(p.x * W, p.y * H) : ctx.moveTo(p.x * W, p.y * H)))
        ctx.stroke()
      })
      return { dataUrl: canvas.toDataURL('image/png'), page: 1 }
    },
  }))

  const Tool = ({ active, onClick, icon, label }) => (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', fontSize: 13, fontWeight: 600,
        borderRadius: 9, cursor: 'pointer', border: '1px solid ' + (active ? 'var(--c)' : 'var(--line)'),
        background: active ? 'var(--c)' : '#fff', color: active ? '#fff' : 'var(--ink-soft)',
      }}
    >
      <Icon name={icon} size={15} stroke={2} />{label}
    </button>
  )

  return (
    <div>
      {/* toolbar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: 14, borderBottom: '1px solid var(--line-soft)', alignItems: 'center' }}>
        <Tool active={mode === 'select'} onClick={() => setMode('select')} icon="maximize-2" label="Move" />
        <Tool active={false} onClick={addText} icon="file-pen-line" label="Text" />
        <Tool active={false} onClick={() => imgInputRef.current?.click()} icon="image" label="Image" />
        <Tool active={mode === 'draw'} onClick={() => { setMode('draw'); setSelected(null) }} icon="pen-line" label="Draw" />
        <input ref={imgInputRef} type="file" accept="image/*" hidden onChange={onImageFile} />

        <span style={{ width: 1, height: 22, background: 'var(--line)', margin: '0 2px' }} />
        {COLORS.map((c) => (
          <button key={c} onClick={() => applyColor(c)} title={c}
            style={{ width: 22, height: 22, borderRadius: '50%', background: c, cursor: 'pointer',
              border: color === c ? '2px solid var(--c)' : '1px solid var(--line)' }} />
        ))}

        {mode === 'draw' && (
          <>
            <span style={{ width: 1, height: 22, background: 'var(--line)', margin: '0 2px' }} />
            <input type="range" min="1" max="10" value={penWidth} onChange={(e) => setPenWidth(+e.target.value)} style={{ width: 90 }} />
            <button onClick={undoStroke} style={{ padding: '6px 10px', fontSize: 12, fontWeight: 600, borderRadius: 8, border: '1px solid var(--line)', background: '#fff', cursor: 'pointer' }}>Undo</button>
          </>
        )}
        {selected && mode === 'select' && (
          <button onClick={deleteSelected} style={{ marginLeft: 'auto', padding: '7px 12px', fontSize: 12, fontWeight: 600, borderRadius: 8, border: '1px solid var(--pdf)', background: 'var(--pdf-tint)', color: 'var(--pdf)', cursor: 'pointer' }}>Delete</button>
        )}
      </div>

      {/* stage */}
      <div style={{ background: '#1f2430', padding: 16, display: 'flex', justifyContent: 'center' }}>
        <div
          ref={wrapRef}
          onPointerMove={onElementMove}
          onPointerUp={onElementUp}
          onPointerLeave={onElementUp}
          onPointerDown={(e) => { if (mode === 'select' && (e.target === wrapRef.current || e.target.tagName === 'IMG')) { setSelected(null); setEditingId(null) } }}
          style={{ position: 'relative', width: '100%', maxWidth: 560, lineHeight: 0, userSelect: 'none', touchAction: 'none', boxShadow: 'var(--shadow-lg)', containerType: 'inline-size' }}
        >
          {bg ? <img src={bg.dataUrl} alt="page" draggable={false} style={{ width: '100%', display: 'block' }} /> : (
            <div style={{ background: '#fff', aspectRatio: '1/1.414', display: 'grid', placeItems: 'center', color: 'var(--muted)', fontSize: 13 }}>Loading page…</div>
          )}

          {/* images */}
          {images.map((im) => {
            const sel = selected?.kind === 'image' && selected.id === im.id
            return (
              <div key={im.id} onPointerDown={startDrag(im, 'image', 'move')}
                style={{ position: 'absolute', left: `${im.xRatio * 100}%`, top: `${im.yRatio * 100}%`, width: `${im.wRatio * 100}%`,
                  cursor: mode === 'select' ? 'move' : 'default', outline: sel ? '2px solid var(--c)' : 'none',
                  pointerEvents: mode === 'draw' ? 'none' : 'auto' }}>
                <img src={im.src} alt="" draggable={false} style={{ width: '100%', display: 'block', pointerEvents: 'none' }} />
                {sel && <span onPointerDown={startDrag(im, 'image', 'resize')} style={handleStyle} />}
              </div>
            )
          })}

          {/* texts */}
          {texts.map((t) => {
            const sel = selected?.kind === 'text' && selected.id === t.id
            const editing = editingId === t.id
            // font scales with the page: ratio is of page height, expressed via cqw.
            const coeff = bg ? t.sizeRatio * (bg.height / bg.width) : 0.03
            return (
              <div key={t.id}
                onPointerDown={(e) => { if (!editing) startDrag(t, 'text', 'move')(e) }}
                onDoubleClick={(e) => {
                  e.stopPropagation()
                  const node = e.currentTarget
                  setSelected({ kind: 'text', id: t.id })
                  setEditingId(t.id)
                  setTimeout(() => node && node.querySelector('span')?.focus(), 0)
                }}
                style={{ position: 'absolute', left: `${t.xRatio * 100}%`, top: `${t.yRatio * 100}%`,
                  cursor: editing ? 'text' : mode === 'select' ? 'move' : 'default',
                  outline: sel ? '1px dashed var(--c)' : 'none',
                  pointerEvents: mode === 'draw' ? 'none' : 'auto', maxWidth: `${(1 - t.xRatio) * 100}%` }}>
                <span
                  contentEditable={editing} suppressContentEditableWarning
                  onBlur={(e) => { setTexts((s) => s.map((x) => (x.id === t.id ? { ...x, text: e.currentTarget.innerText } : x))); setEditingId(null) }}
                  style={{ display: 'inline-block', fontFamily: 'Helvetica, Arial, sans-serif', fontWeight: 600,
                    fontSize: `calc(${coeff} * 100cqw)`, color: t.color, lineHeight: 1.15, outline: 'none', whiteSpace: 'pre-wrap' }}
                >{t.text}</span>
                {sel && !editing && <span onPointerDown={startDrag(t, 'text', 'resize')} style={handleStyle} />}
              </div>
            )
          })}

          {/* drawing layer */}
          <canvas
            ref={canvasRef}
            onPointerDown={mode === 'draw' ? drawStart : undefined}
            onPointerMove={mode === 'draw' ? drawMove : undefined}
            onPointerUp={mode === 'draw' ? drawEnd : undefined}
            onPointerLeave={mode === 'draw' ? drawEnd : undefined}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: mode === 'draw' ? 'auto' : 'none', cursor: mode === 'draw' ? 'crosshair' : 'default' }}
          />
        </div>
      </div>
    </div>
  )
})

const handleStyle = {
  position: 'absolute', right: -8, bottom: -8, width: 16, height: 16, borderRadius: '50%',
  background: 'var(--c)', border: '2px solid #fff', cursor: 'nwse-resize', boxShadow: '0 1px 4px rgba(0,0,0,.3)',
}

function loadImg(src) {
  return new Promise((res, rej) => {
    const im = new Image()
    im.onload = () => res(im)
    im.onerror = rej
    im.src = src
  })
}

export default PdfEditor
